/**
 * Altar Forge tuning knobs (docs/design/crafting.md §12). Every number the forge, payout and
 * tier-set code uses lives here so balance passes touch one file. Pure data, no imports beyond
 * types.
 */
import type { ItemSlot, Rarity, ScaleGrade, TierSlot } from './types';
import { SCALE_GRADES } from './types';

// ============================================================================
// Grades and item levels (§1.1)
// ============================================================================

/** Crafted item level per Scale grade (capped by the rank tier's maxIlvl). */
export const GRADE_CRAFT_ILVL: Readonly<Record<ScaleGrade, number>> = { ashen: 210, jade: 225, gilded: 240, eternal: 255 };
/** Gold multiplier per grade (crafts, refine, drill). */
export const GRADE_GOLD: Readonly<Record<ScaleGrade, number>> = { ashen: 1, jade: 1.5, gilded: 2.2, eternal: 3 };
/** Lowest key level that drops each grade. */
export const GRADE_MIN_KEY: Readonly<Record<ScaleGrade, number>> = { ashen: 0, jade: 5, gilded: 9, eternal: 13 };
/** Highest temper TARGET ilvl that each grade pays for (bands: 205–220, 225–240, 245–255, 260–270). */
export const GRADE_BAND_MAX: Readonly<Record<ScaleGrade, number>> = { ashen: 220, jade: 240, gilded: 255, eternal: Infinity };

export const gradeIndex = (g: ScaleGrade): number => SCALE_GRADES.indexOf(g);

/** Grade of the Coil Scales a key of this level drops. */
export function gradeForKey(level: number): ScaleGrade {
  if (level >= GRADE_MIN_KEY.eternal) return 'eternal';
  if (level >= GRADE_MIN_KEY.gilded) return 'gilded';
  if (level >= GRADE_MIN_KEY.jade) return 'jade';
  return 'ashen';
}

/** Temper band (Scale grade that pays) of a target item level. */
export function gradeForIlvl(ilvl: number): ScaleGrade {
  for (const g of SCALE_GRADES) if (ilvl <= GRADE_BAND_MAX[g]) return g;
  return 'eternal';
}

// ============================================================================
// Crafting (§3)
// ============================================================================

export const SLOT_MULT: Readonly<Record<ItemSlot, number>> = {
  weapon: 1.5,
  chest: 1.25,
  legs: 1.25,
  helm: 1,
  trinket: 1,
  gloves: 0.85,
  boots: 0.85,
  ring: 0.75,
};
export const CRAFT_FANGSTEEL = 30;
export const CRAFT_SCALES = 6;
export const CRAFT_GOLD = 150;

export const LUCK_CAP = 14;
export const VENOM_MAX_CRAFT = 10;
export const BLESSED_LUCK = 3;
export const FAVORED_LUCK = 2;
export const FORTUNE_EVERY = 10;
export const FORTUNE_LUCK = 4;
export const VAULT_TOKEN_LUCK = 6;
/** Codex: +CODEX_LUCK_STEP Luck per CODEX_LUCK_EVERY entries, capped at CODEX_LUCK_MAX. */
export const CODEX_LUCK_STEP = 0.5;
export const CODEX_LUCK_EVERY = 10;
export const CODEX_LUCK_MAX = 2;

/** Rarity % before floors and pity, for Luck Λ (§3.2). Sums to 100. */
export const RARITY_FORMULA = {
  epic: (luck: number): number => Math.min(60, 5 + 3.5 * luck),
  common: (luck: number): number => Math.max(0, 40 - 5 * luck),
  rareMax: 52,
  rare: (luck: number): number => 20 + 4 * luck,
} as const;

export const EPIC_PITY_HARD = 12;
export const EPIC_SOFT_FROM = 9;
export const EPIC_SOFT_STEP = 15;
/** New profiles start with this much Epic pity (endowed progress). */
export const EPIC_PITY_START = 3;
/** A roll within this many points below the Epic band counts double toward Epic pity. */
export const EPIC_NEAR_MISS_WINDOW = 5;
export const RARE_FLOOR_AFTER = 3;

/** Legendary chance (%) for the 1st, 2nd, 3rd, 4th consecutive Eye. */
export const EYE_CHANCES: readonly number[] = [30, 55, 80, 100];
export const EYE_FRAGMENTS_PER_EYE = 5;

/** Moltskin per craft lock (1 = first stat, 2 = both / trinket effect). */
export const MOLTSKIN_MAX_CRAFT = 2;

/** Secondary quality range and step (rating = budget × quality). */
export const QUALITY_MIN = 0.9;
export const QUALITY_MAX = 1.1;
export const QUALITY_STEPS = 20;

/** First-socket chance by rarity; second-socket chance (needs the first). Weapon/ring +15 pp first. */
export const CRAFT_SOCKET_FIRST: Readonly<Record<Rarity, number>> = { common: 0, uncommon: 0.2, rare: 0.4, epic: 1, legendary: 1 };
export const CRAFT_SOCKET_SECOND: Readonly<Record<Rarity, number>> = { common: 0, uncommon: 0, rare: 0.25, epic: 0.35, legendary: 0 };
export const SOCKET_SLOT_BONUS: Partial<Record<ItemSlot, number>> = { weapon: 0.15, ring: 0.15 };
/** Most sockets a drill or an Awakening can reach, by rarity (§3.9). */
export const DRILL_MAX_SOCKETS: Readonly<Record<Rarity, number>> = { common: 0, uncommon: 1, rare: 1, epic: 2, legendary: 1 };
/** Trinket effect chance by rarity (§3.4). */
export const TRINKET_EFFECT_BY_RARITY: Readonly<Record<Rarity, number>> = { common: 0.5, uncommon: 0.7, rare: 0.85, epic: 1, legendary: 1 };
/** Masterwork ceiling by rarity (§3.4). Higher legacy masterwork is grandfathered. */
export const MASTERWORK_CEILING: Readonly<Record<Rarity, number>> = { common: 0, uncommon: 1, rare: 2, epic: 3, legendary: 3 };

/** The starter cache (First Offering) a new profile gets for the craft tutorial. */
export const FIRST_OFFERING = { fangsteel: 40, scales: { ashen: 6 } } as const;

// +forge-sinks (crafting.md §4.6): the free first temper and the late Sigil / Eye-fragment sinks.
/** Free temper / masterwork attempts a new profile starts with (its Fangsteel, Scales and gold are waived). */
export const FREE_TEMPERS_START = 1;
/**
 * +econ-pacing: the free temper's item-level cap is at least this (one step over the Novice cap of
 * 200), so a day-one Novice (a timed +2 is ~440 rating, Contender is 500) can actually use it on run 1.
 */
export const FREE_TEMPER_MIN_CAP = 205;
/** Sigil Reshape: 1 Altar Sigil + this × GRADE_GOLD gold rerolls a tier piece's two secondaries (keep-or-replace). */
export const SIGIL_RESHAPE_GOLD = 250;
/** Eye Attunement: this many Eye fragments + gold advance Eye pity one step (30 → 55 → 80 → 100%). */
export const EYE_ATTUNE_FRAGMENTS = 2;
export const EYE_ATTUNE_GOLD = 300;

// ============================================================================
// Tempering (§4)
// ============================================================================

/** Base success % of a temper step to `target` ilvl. */
export function temperBase(target: number): number {
  if (target <= 220) return 100;
  return Math.max(5, Math.min(100, 80 - (target - 225)));
}
export const TEMPER_COST: Readonly<Record<ScaleGrade, { fangsteel: number; scales: number; gold: number }>> = {
  ashen: { fangsteel: 8, scales: 2, gold: 60 },
  jade: { fangsteel: 12, scales: 3, gold: 120 },
  gilded: { fangsteel: 16, scales: 4, gold: 220 },
  eternal: { fangsteel: 20, scales: 5, gold: 350 },
};
export const FAILSTACK_PP = 10;
export const RESONANCE_PER_FAIL = 25;
export const RESONANCE_NEAR_MISS = 35;
export const RESONANCE_MAX = 100;
/** A failed roll within this many points of the needed number is a near miss. */
export const NEAR_MISS_WINDOW = 10;
export const REFUND_FRACTION = 0.5;
export const TEMPER_VENOM_PP = 5;
export const TEMPER_VENOM_MAX = 5;
export const BLESSED_TEMPER_PP = 15;
export const SURGE_CHANCE = 0.1;
export const AWAKEN_EVERY = 3;
export type AwakeningKind = 'empowered' | 'socket' | 'echo' | 'ascend';
export const AWAKEN_TABLE: readonly { kind: AwakeningKind; chance: number }[] = [
  { kind: 'empowered', chance: 0.4 },
  { kind: 'socket', chance: 0.3 },
  { kind: 'echo', chance: 0.2 },
  { kind: 'ascend', chance: 0.1 },
];
export const EMPOWER_MULT = 1.15;
export const ECHO_VENOM = 2;

/** Masterwork step n → n+1 (0-based): base % and cost (Scales of the cap band). */
export const MW_BASE: readonly number[] = [70, 55, 40];
export const MW_COST: readonly { fangsteel: number; scales: number; gold: number }[] = [
  { fangsteel: 40, scales: 6, gold: 600 },
  { fangsteel: 60, scales: 8, gold: 900 },
  { fangsteel: 80, scales: 10, gold: 1300 },
];

export const DRILL_VENOM = 5;
export const DRILL_GOLD = 200;
export const DRILL_BASE = 50;
export const REFINE_GOLD = 100;
export const GILD_MAX = 5;
export const GILD_SCALES = 5;
export const GILD_GOLD = 500;
export const GILD_BASE = 60;
export const CATALYST_FANGSTEEL = 10;
/** Combine 3 → 1 of the next grade; fee = COMBINE_GOLD × target grade index. */
export const COMBINE_RATIO = 3;
export const COMBINE_GOLD = 25;
/** Salvage Fangsteel by rarity (on top of 5 + floor((ilvl − 200)/5)). */
export const SALVAGE_RARITY: Readonly<Record<Rarity, number>> = { common: 0, uncommon: 2, rare: 4, epic: 10, legendary: 40 };
/** Reforge price in the forge economy (Shards are gone). */
export const REFORGE_FANGSTEEL = 5;

// ============================================================================
// Payouts, dailies, vault (§2)
// ============================================================================

export const DAILY_SIGIL_BANK = 3;
export const BLESSED_CAP = 6;
export const DAILY_BLESSED = 3;
export const BLESSED_OVERFLOW_VENOM = 2;
export const WEEKLY_RITUAL_RUNS = 4;
export const WEEKLY_RITUAL_SCALES = 15;
export const BRACKET_CACHE = { scales: 10, fangsteel: 30 } as const;
export const VAULT_SCALE_CACHE = { scales: 12, fangsteel: 40 } as const;
export const VAULT_ALTAR_CACHE = { sigils: 1, venom: 5, moltskin: 1, eyeFragmentsChampion: 2 } as const;
/** Chance of a 2nd Eye fragment from the final boss by tier. */
export const EYE_EXTRA_CHANCE: Readonly<Record<string, number>> = { paragon: 0.25, eternal: 0.5 };

/** Favored Slot of the Day order (indexed by a hash of the day number). */
export const FAVORED_SLOT_ORDER: readonly ItemSlot[] = ['helm', 'weapon', 'chest', 'ring', 'legs', 'trinket', 'gloves', 'boots'];

/** History strip length. */
export const FORGE_HISTORY_MAX = 10;
/** RNG salt for forge rolls (xor'd with forge.rollIndex). */
export const FORGE_SALT = 0x5eed_f04e;

// ============================================================================
// Tier sets (§5)
// ============================================================================

/** Multiplies every numeric tier-set bonus (the one dial for set power, §8.4). */
export const TIER_POWER_SCALE = 1;
/** Scale an additive bonus value (chance, +pct, refund) by TIER_POWER_SCALE. */
export const tierAdd = (v: number): number => v * TIER_POWER_SCALE;
/** Scale a multiplier (1.15 → 1 + 0.15 × scale). */
export const tierMult = (m: number): number => 1 + (m - 1) * TIER_POWER_SCALE;
export const SET_THRESHOLDS: readonly number[] = [2, 4];
/** Item-score bonus of a piece of the wearer's own tier set (auto-equip prefers it). */
export const TIER_PIECE_SCORE = 8;
export const TIER_SET_SLOTS: readonly TierSlot[] = ['helm', 'chest', 'legs', 'gloves', 'boots'];

/** Tier set id per class (stable save ids; data in data/tiersets). */
export const TIER_SET_IDS: Readonly<Record<string, string>> = {
  warden: 'set.warden.coilguard',
  blademaster: 'set.blademaster.fangreaver',
  lightbinder: 'set.lightbinder.dawnserpent',
  stormcaller: 'set.stormcaller.stormscale',
};
